import { useState, useMemo, useEffect } from "react";

const DEFAULT_PAGE_SIZE = 10;

/**
 * Reusable pagination hook — pure client-side slicing of already
 * fetched/sorted/filtered data. Never touches Firestore, never re-sorts,
 * never re-filters; it only decides which slice of the array you hand it
 * belongs on the current page.
 *
 * Usage:
 *   const { pageData, ...pagination } = usePagination(filteredRows, {
 *     resetKey: search, // or `${activeTab}|${search}` etc.
 *   });
 *   // render pageData instead of filteredRows
 *   // render <Pagination {...pagination} />
 *
 * @param {Array} data - the fully sorted + filtered array to paginate.
 * @param {Object} options
 * @param {number} [options.pageSize=10] - rows per page.
 * @param {*} [options.resetKey] - whenever this value changes (e.g. the
 *   search term, or a search+tab composite key), the current page resets
 *   to 1. Pass whatever inputs drive your filtering.
 */
export default function usePagination(data, options = {}) {
  const { pageSize = DEFAULT_PAGE_SIZE, resetKey } = options;
  const [currentPage, setCurrentPage] = useState(1);

const items = useMemo(() => (Array.isArray(data) ? data : []), [data]);
  const totalRecords = items.length;
  const totalPages = Math.max(1, Math.ceil(totalRecords / pageSize));

  // Reset to page 1 whenever the caller's resetKey changes — typically
  // the search text or active filter/tab.
  useEffect(() => {
    setCurrentPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetKey]);

  // Clamp if the data shrinks below the current page (e.g. after a delete
  // or a filter narrowing the list) so the user is never stranded on an
  // empty page.
  useEffect(() => {
    setCurrentPage((prev) => Math.min(prev, totalPages));
  }, [totalPages]);

  const safePage = Math.min(currentPage, totalPages);

  const pageData = useMemo(() => {
    const start = (safePage - 1) * pageSize;
    return items.slice(start, start + pageSize);
  }, [items, safePage, pageSize]);

  const startIndex = totalRecords === 0 ? 0 : (safePage - 1) * pageSize + 1;
  const endIndex = Math.min(safePage * pageSize, totalRecords);

  const goToPage = (page) => {
    const clamped = Math.min(Math.max(1, page), totalPages);
    setCurrentPage(clamped);
  };

  const nextPage = () => goToPage(safePage + 1);
  const prevPage = () => goToPage(safePage - 1);

  return {
    currentPage: safePage,
    totalPages,
    totalRecords,
    pageSize,
    pageData,
    startIndex,
    endIndex,
    goToPage,
    nextPage,
    prevPage,
    hasPrev: safePage > 1,
    hasNext: safePage < totalPages,
    // Pagination UI hides itself entirely when everything fits on one page.
    showPagination: totalRecords > pageSize,
  };
}